import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { theme } from '../styles/theme';
import { Spinner } from '../styles/components';
import { useTitleDetails } from '../hooks/useTitleDetails';
import { formatTooltipValue } from '../utils/formatters';
import type { BudgetApiClient } from '../api/client';
import { einzelplanName, lookupLabel, t, useT } from '../i18n';
import type { TitleYearMeta } from '../types';

// Matches ExpenditureAreaChart (DZ catalog: black + red accent)
const IST_LINE_COLOR = '#000000';
const SOLL_LINE_COLOR = '#DA454E';

function formatSollSource(sollSource: string | null, year: number): string {
  if (!sollSource) return `BHH ${year}`;
  if (sollSource === 'BHH') return `BHH ${year}`;
  if (sollSource.startsWith('NHH')) return `${sollSource} ${year}`;
  if (sollSource === 'Rechnung') return `HHR ${year}`;
  return `${sollSource} ${year}`;
}

function formatTitleMeta(meta: TitleYearMeta | undefined): string[] {
  if (!meta) return [];
  const lines: string[] = [];
  const m = t();
  if (meta.einzelplan) {
    const value = meta.einzelplanText ? `${meta.einzelplan} ${einzelplanName(meta.einzelplanText)}` : meta.einzelplan;
    lines.push(`${m.tooltip.einzelplanShort}: ${value}`);
  }
  if (meta.kapitel) {
    const value = meta.kapitelText ? `${meta.kapitel} ${lookupLabel('kapitel', meta.kapitelText)}` : meta.kapitel;
    lines.push(`${m.tooltip.kapitelShort}: ${value}`);
  }
  return lines;
}

const PopoverContainer = styled.div<{ isVisible: boolean; top: number; left: number }>`
  position: fixed;
  top: ${props => props.top}px;
  left: ${props => props.left}px;
  min-width: 280px;
  max-width: 360px;
  background: ${theme.colors.surface};
  border-radius: var(--radius-s);
  box-shadow: var(--shadow-s);
  padding: ${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.md};
  z-index: 10000;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transition: opacity 0.15s ease;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.xs};
`;

const Title = styled.div`
  font-family: var(--sans-stack);
  font-size: 1rem;
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  line-height: 1.3;
  flex: 1;
  min-width: 0;
  word-break: break-word;
`;

const CloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  font-size: 15px;
  border: none;
  background: transparent;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(24, 28, 68, 0.08);
    color: ${theme.colors.text.primary};
  }
`;

const MetaLine = styled.div`
  font-family: var(--sans-stack);
  font-size: 1rem;
  color: ${theme.colors.text.muted};
  line-height: 1.4;
  margin-bottom: 2px;
`;

const YearLine = styled.div`
  font-family: var(--sans-stack);
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
  margin: ${theme.spacing.xs} 0 ${theme.spacing.sm} 0;
`;

const ValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: 2px;
`;

const ColorDot = styled.span<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background-color: ${props => props.color};
  flex-shrink: 0;
`;

const ValueLabel = styled.span`
  font-family: var(--sans-stack);
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
  flex: 1;
`;

const ValueAmount = styled.span`
  font-family: var(--sans-stack);
  font-size: 1rem;
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  font-variant-numeric: tabular-nums;
`;

const NoData = styled.span`
  font-family: var(--sans-stack);
  font-size: 1rem;
  color: ${theme.colors.text.muted};
  font-style: italic;
`;

const SourceLine = styled.div`
  font-family: var(--sans-stack);
  font-size: 1rem;
  color: ${theme.colors.text.muted};
  margin: 0 0 ${theme.spacing.sm} 22px;
  line-height: 1.3;
`;

const SourceLineLast = styled(SourceLine)`
  margin-bottom: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${theme.spacing.sm};
`;

const CloseIcon = () => (
  <span className="icon" data-icon="close" />
);

interface TitlePointPopoverProps {
  apiClient: BudgetApiClient;
  titleId: string;
  budgetType: 'E' | 'A';
  year: number;
  name: string;
  sollValue: number;
  istValue: number | null;
  meta?: TitleYearMeta;
  valueFormatter?: (value: number) => string;
  anchorX: number;
  anchorY: number;
  onClose: () => void;
}

const POPOVER_WIDTH = 320;
const POPOVER_HEIGHT_ESTIMATE = 220;
const ANCHOR_OFFSET = 12;

export function TitlePointPopover({
  apiClient,
  titleId,
  budgetType,
  year,
  name,
  sollValue,
  istValue,
  meta,
  valueFormatter = formatTooltipValue,
  anchorX,
  anchorY,
  onClose,
}: TitlePointPopoverProps) {
  const m = useT();
  const containerRef = useRef<HTMLDivElement>(null);
  const { data, isLoading } = useTitleDetails(apiClient, titleId, budgetType);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);

  // Position near the clicked dot, flipping sides/edges to stay on-screen.
  useEffect(() => {
    const margin = 8;
    let left = anchorX + ANCHOR_OFFSET;
    let top = anchorY - POPOVER_HEIGHT_ESTIMATE / 2;

    if (left + POPOVER_WIDTH > window.innerWidth - margin) {
      left = anchorX - POPOVER_WIDTH - ANCHOR_OFFSET;
    }
    if (left < margin) left = margin;
    if (top < margin) top = margin;
    if (top + POPOVER_HEIGHT_ESTIMATE > window.innerHeight - margin) {
      top = window.innerHeight - POPOVER_HEIGHT_ESTIMATE - margin;
    }
    setPosition({ top, left });
    setIsPositioned(true);
  }, [anchorX, anchorY]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    // mouseup so the click that opened the popover (already dispatched on mousedown
    // by recharts' chart onClick) doesn't immediately close it.
    document.addEventListener('mouseup', handleClickOutside);
    return () => document.removeEventListener('mouseup', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const yearRow = data?.yearlyData.find(r => r.year === year);
  const sollSourceText = yearRow
    ? `${formatSollSource(yearRow.sollSource, year)}${yearRow.pageSoll != null ? `, ${m.common.page(yearRow.pageSoll)}` : ''}`
    : null;
  const istSourceText = yearRow && yearRow.ist !== null && yearRow.pageIst != null
    ? `HHR ${year}, ${m.common.page(yearRow.pageIst)}`
    : null;
  const hasIst = istValue !== null && istValue !== undefined;
  const metaLines = formatTitleMeta(meta);

  const popover = (
    <PopoverContainer
      ref={containerRef}
      isVisible={isPositioned}
      top={position.top}
      left={position.left}
      role="dialog"
    >
      <HeaderRow>
        <Title>{name}</Title>
        <CloseButton onClick={onClose} title={m.common.close} aria-label={m.common.close}>
          <CloseIcon />
        </CloseButton>
      </HeaderRow>
      {metaLines.map((line, i) => (
        <MetaLine key={i}>{line}</MetaLine>
      ))}
      <YearLine>{m.common.year(year)}</YearLine>

      <ValueRow>
        <ColorDot color={SOLL_LINE_COLOR} />
        <ValueLabel>{m.tooltip.sollLabel}</ValueLabel>
        <ValueAmount>{valueFormatter(sollValue)}</ValueAmount>
      </ValueRow>
      {isLoading ? (
        <LoadingContainer><Spinner /></LoadingContainer>
      ) : (
        <SourceLine>{sollSourceText ?? `BHH ${year}`}</SourceLine>
      )}

      <ValueRow>
        <ColorDot color={IST_LINE_COLOR} />
        <ValueLabel>{m.tooltip.istLabel}</ValueLabel>
        {hasIst ? (
          <ValueAmount>{valueFormatter(istValue)}</ValueAmount>
        ) : (
          <NoData>{m.common.noData}</NoData>
        )}
      </ValueRow>
      {hasIst && (
        isLoading ? (
          <LoadingContainer><Spinner /></LoadingContainer>
        ) : (
          <SourceLineLast>{istSourceText ?? `HHR ${year}`}</SourceLineLast>
        )
      )}
    </PopoverContainer>
  );

  return createPortal(popover, document.body);
}
